import { routes } from "@constants/routes";
import {
  getOrCreateDeviceId,
  GoogleIcon,
  handleApiRequest,
  LinkedinIcon,
  useAuth,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import {
  AccountGoogleAuthMutation,
  AccountGoogleAuthMutationVariables,
  AccountLinkedinAuthMutation,
  AccountLinkedinAuthMutationVariables,
  useAccountGoogleAuthMutation,
  useAccountLinkedinAuthMutation,
} from "@graphql/generated";
import { Button, Divider as NextDivider } from "@heroui/react";
import { useGoogleLogin } from "@react-oauth/google";
import React from "react";
import { useLinkedIn } from "react-linkedin-login-oauth2";
import { useNavigate } from "react-router-dom";

interface SocialLoginButtonsProps {
  isLogin?: boolean;
  referralCode?: string;
  jobReferralCode?: string;
  colReverse?: boolean;
  isDisabled?: boolean;
}

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  isLogin = true,
  referralCode,
  jobReferralCode,
  colReverse = false,
  isDisabled = false,
}) => {
  const actionText = isLogin ? "Login " : "Sign up ";
  const linkedinRedirectUri = `${window.location.origin}${routes.auth.linkedIn}`;
  const { setToken } = useAuth({ userType: UserTypeEnum.USER });
  const [googleAuth, { loading: googleAuthLoading }] = useAccountGoogleAuthMutation();
  const [linkedinAuth, { loading: linkedinAuthLoading }] = useAccountLinkedinAuthMutation();
  const navigate = useNavigate();
  const formValues = sessionStorage.getItem("onboardingFormData");

  const onboardingFormData = JSON.parse(formValues ?? "{}");

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      await handleApiRequest<AccountGoogleAuthMutation, AccountGoogleAuthMutationVariables>({
        apiFunction: googleAuth,
        variables: {
          accessToken: codeResponse.access_token,
          referralCode,
          jobReferralCode,
          deviceId: getOrCreateDeviceId(),
          ...(!isLogin &&
            formValues && {
              country: onboardingFormData.country,
              industry: onboardingFormData.industry,
              interestedJobs: onboardingFormData.jobInterest,
              ...(onboardingFormData.country === "38" && { canadaVisaStatus: onboardingFormData.residencyStatus }),
              workExperienceYearsRange: onboardingFormData.yearsOfExperience.split("-").map(Number),
            }),
        },
        componentName: "AuthForm",
        actionName: "googleAuth",
        fallbackErrorMessage: "Google authentication failed. Please try again.",
        extraErrorContext: {
          hasReferralCode: !!referralCode,
        },
        displaySuccessToast: false,
        onSuccess(response) {
          doLogin({
            token: response?.data?.account.googleAuth?.token ?? "",
            refreshToken: response?.data?.account.googleAuth?.refreshToken ?? "",
            accessExp: response?.data?.account.googleAuth?.payload.exp ?? 0,
            refreshExp: response?.data?.account.googleAuth?.refreshExpiresIn ?? 0,
          });
          return { success: true };
        },
      });
    },
  });

  const { linkedInLogin } = useLinkedIn({
    clientId: import.meta.env.VITE_PUBLIC_LINKEDIN_CLIENT_ID,
    scope: "openid profile email",
    redirectUri: linkedinRedirectUri,
    onSuccess: async (code) => {
      await handleApiRequest<AccountLinkedinAuthMutation, AccountLinkedinAuthMutationVariables>({
        apiFunction: linkedinAuth,
        variables: {
          code,
          redirectUri: linkedinRedirectUri,
          referralCode,
          deviceId: getOrCreateDeviceId(),
          ...(!isLogin &&
            formValues && {
              country: onboardingFormData.country,
              industry: onboardingFormData.industry,
              interestedJobs: onboardingFormData.jobInterest,
              ...(onboardingFormData.country === "38" && { canadaVisaStatus: onboardingFormData.residencyStatus }),
              workExperienceYearsRange: onboardingFormData.yearsOfExperience.split("-").map(Number),
            }),
        },
        componentName: "AuthForm",
        actionName: "linkedinAuth",
        fallbackErrorMessage: "LinkedIn authentication failed. Please try again.",
        extraErrorContext: {
          hasReferralCode: !!referralCode,
          redirectUri: linkedinRedirectUri,
        },
        displaySuccessToast: false,
        onSuccess: (response) => {
          doLogin({
            token: response?.data?.account.linkedinAuth?.token ?? "",
            refreshToken: response?.data?.account.linkedinAuth?.refreshToken ?? "",
            accessExp: response?.data?.account.linkedinAuth?.payload.exp ?? 0,
            refreshExp: response?.data?.account.linkedinAuth?.refreshExpiresIn ?? 0,
          });
          return { success: true };
        },
      });
    },
  });

  const doLogin = (token: AuthToken) => {
    setToken(token);
    navigate(routes.dashboard.main);
  };

  return (
    <div className={`flex flex-col justify-center items-center w-full ${colReverse ? "flex-col-reverse" : ""}`}>
      <Divider />
      <div className="flex flex-col justify-center items-center w-full gap-4 md:gap-2 lg:gap-4 md:flex-row">
        <Button
          onPress={() => googleLogin()}
          fullWidth
          variant="bordered"
          isLoading={googleAuthLoading}
          startContent={<GoogleIcon width={20} height={20} className="min-w-5 min-h-5" />}
          isDisabled={isDisabled}
        >
          {actionText} with Google
        </Button>
        <Button
          onPress={() => linkedInLogin()}
          fullWidth
          variant="bordered"
          isLoading={linkedinAuthLoading}
          startContent={<LinkedinIcon width={20} height={20} className="min-w-5 min-h-5" />}
          isDisabled={isDisabled}
        >
          {actionText} with Linkedin
        </Button>
      </div>
    </div>
  );
};

const Divider: React.FC = () => (
  <div className="flex flex-row justify-between items-center w-full my-4 md:my-6 sm:my-6">
    <NextDivider className="w-[45%]" />
    <span>or</span>
    <NextDivider className="w-[45%]" />
  </div>
);

export default SocialLoginButtons;
